/**
 * Location Row Component
 * Displays a single location with inline editing capabilities
 * Follows Single Responsibility and Open/Closed Principles
 */

import React, { useState, useRef, useEffect, memo } from 'react';
import { Pencil, Copy, Check, X, ChevronRight, CheckCircle2, FileEdit, Circle } from 'lucide-react';
import { serviceLogger } from '../../utils/serviceLogger';

/**
 * Determine cleaning standards status for a location
 * @param {Object} location - Location object
 * @returns {'complete'|'draft'|'not-set'} Status of cleaning standards
 */
const getCleaningStandardsStatus = (location) => {
  // Not set: no standards assigned
  if (!location.cleaningStandards || location.cleaningStandardsStatus === 'pending') {
    return 'not-set';
  }

  const standards = location.cleaningStandards;

  // Check if all required fields are present and valid
  const hasFrequency = !!standards.frequency;
  const hasWindows =
    !!standards.windows && Array.isArray(standards.windows) && standards.windows.length > 0;
  const hasMoppingDuration = !!standards.minDuration && !!standards.maxDuration;
  const hasRinseWring = typeof standards.minRinseWringCycles === 'number';
  const hasOtherTasks =
    typeof standards.minOtherTasksDuration === 'number' &&
    typeof standards.maxOtherTasksDuration === 'number';

  // Complete: all sections are configured
  if (hasFrequency && hasWindows && hasMoppingDuration && hasRinseWring && hasOtherTasks) {
    return 'complete';
  }

  // Draft: some sections are configured but not all
  if (hasFrequency || hasWindows || hasMoppingDuration || hasRinseWring || hasOtherTasks) {
    return 'draft';
  }

  // Not set: has standards object but no actual configuration
  return 'not-set';
};

/**
 * Normalize status to API values: "set" | "not set" | "draft"
 * @param {Object} location
 * @param {string} [apiStatus] - From GET /locations/bulk-status
 * @returns {'set'|'not set'|'draft'}
 */
const getDisplayStatus = (location, apiStatus) => {
  if (apiStatus === 'set' || apiStatus === 'draft' || apiStatus === 'not set') {
    return apiStatus;
  }
  const derived = getCleaningStandardsStatus(location);
  if (derived === 'complete') return 'set';
  if (derived === 'draft') return 'draft';
  return 'not set';
};

const LocationRowComponent = ({
  location,
  isSelected,
  onSelect,
  onUpdate,
  onDuplicate,
  onOpenStandards,
  hasAnySelection,
  readOnlyMode,
  hasStandards,
  cleaningStandardsStatus,
  showCleaningStandards = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(location.name);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditedName(location.name);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedName(location.name);
  };

  const handleConfirm = async () => {
    const trimmedName = editedName.trim();
    if (!trimmedName || trimmedName === location.name) {
      handleCancel();
      return;
    }

    try {
      setIsSubmitting(true);
      await onUpdate(location.id, trimmedName);
      setIsEditing(false);
    } catch (error) {
      serviceLogger.error('Error updating location:', error);
      setEditedName(location.name);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleDuplicate = async () => {
    try {
      onDuplicate(location);
    } catch (error) {
      serviceLogger.error('Error duplicating location:', error);
    }
  };

  // Handle row click - Opens drawer in normal mode, selects in selection mode
  const handleRowClick = (e) => {
    // Don't trigger if clicking on interactive elements
    const target = e.target;
    if (
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT' ||
      target.closest('button') ||
      target.closest('input')
    ) {
      return;
    }

    // If in editing mode, do nothing
    if (isEditing) return;

    // If in selection mode (any checkboxes marked), toggle selection
    if (hasAnySelection) {
      onSelect(location.id);
    } else if (showCleaningStandards) {
      // Step 3 only: open standards drawer when clicking row
      onOpenStandards?.(location.id);
    } else {
      // Step 1: row click just selects the location (no drawer)
      onSelect(location.id);
    }
  };

  // Use grid layout when showCleaningStandards is true to align with header
  if (showCleaningStandards && !isEditing) {
    return (
      <div
        className={`group bg-white border border-[#e5e7eb] rounded-[8px] px-4 py-3 grid gap-4 items-center transition-all ${
          !hasAnySelection
            ? 'cursor-pointer hover:shadow-md hover:border-[#61b8e7]'
            : 'hover:shadow-sm'
        } ${
          readOnlyMode && !hasAnySelection ? 'hover:bg-gray-50' : ''
        } grid-cols-[auto_1fr_160px_100px]`}
        onClick={handleRowClick}
      >
        {/* Checkbox */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(location.id);
            }}
            className="group"
          >
            <div className="relative">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all group-hover:border-[#61b8e7] pointer-events-none"
                aria-label={`Select ${location.name}`}
              />
              {isSelected && (
                <svg
                  className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                  fill="none"
                  viewBox="0 0 12 10"
                >
                  <path
                    d="M1.5 5L4.5 8L10.5 1.5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </button>
        </div>

        {/* Location Name */}
        <div className="min-w-0">
          <p
            className="font-['Roboto:Regular',sans-serif] font-normal text-[15px] text-black truncate"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {location.name}
          </p>
        </div>

        {/* Cleaning Standards Status: set | not set | draft (from bulk-status API or derived) */}
        <div className="flex-shrink-0">
          {(() => {
            const status = getDisplayStatus(location, cleaningStandardsStatus);

            switch (status) {
              case 'set':
                return (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-300 font-['Roboto:Medium',sans-serif] font-medium text-[13px]">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Set
                  </span>
                );
              case 'draft':
                return (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-300 font-['Roboto:Medium',sans-serif] font-medium text-[13px]">
                    <FileEdit className="w-3.5 h-3.5" />
                    Draft
                  </span>
                );
              case 'not set':
              default:
                return (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 text-gray-600 border border-gray-300 font-['Roboto:Medium',sans-serif] font-medium text-[13px]">
                    <Circle className="w-3.5 h-3.5" />
                    Not set
                  </span>
                );
            }
          })()}
        </div>

        {/* Chevron - Step 3 Mode (Read Only) */}
        <div className="flex-shrink-0 flex justify-end">
          <ChevronRight className="w-[18px] h-[18px] text-gray-400 transition-colors group-hover:text-gray-600" />
        </div>
      </div>
    );
  }

  // Default flex layout for non-cleaning-standards mode
  return (
    <div
      className={`group bg-white border border-[#e5e7eb] rounded-[8px] px-4 py-3 flex items-center gap-4 transition-all ${
        !isEditing && !hasAnySelection
          ? 'cursor-pointer hover:shadow-md hover:border-[#61b8e7]'
          : 'hover:shadow-sm'
      } ${readOnlyMode && !hasAnySelection ? 'hover:bg-gray-50' : ''}`}
      onClick={handleRowClick}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(location.id);
          }}
          className="group"
        >
          <div className="relative">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all group-hover:border-[#61b8e7] pointer-events-none"
              aria-label={`Select ${location.name}`}
            />
            {isSelected && (
              <svg
                className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                fill="none"
                viewBox="0 0 12 10"
              >
                <path
                  d="M1.5 5L4.5 8L10.5 1.5"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </button>
      </div>

      {/* Location Name */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className="flex-1 font-['Roboto:Regular',sans-serif] font-normal text-[15px] text-black bg-white border-2 border-[#61b8e7] rounded-md px-2 py-1 outline-none"
              style={{ fontVariationSettings: "'wdth' 100" }}
            />
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting || !editedName.trim()}
              className="hover:bg-green-50 disabled:opacity-30 rounded-md p-1 transition-colors flex-shrink-0"
              aria-label="Confirm"
            >
              <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="hover:bg-red-50 disabled:opacity-30 rounded-md p-1 transition-colors flex-shrink-0"
              aria-label="Cancel"
            >
              <X className="w-3.5 h-3.5 text-red-600" strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <p
            className="font-['Roboto:Regular',sans-serif] font-normal text-[15px] text-black truncate"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {location.name}
          </p>
        )}
      </div>

      {/* Actions - Step 2 Mode */}
      {!isEditing && !readOnlyMode && (
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleStartEdit();
            }}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Edit area"
          >
            <Pencil className="w-4 h-4 text-[#6e6e71]" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicate();
            }}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Duplicate area"
          >
            <Copy className="w-4 h-4 text-[#6e6e71]" />
          </button>
        </div>
      )}

      {/* Chevron - Step 3 Mode (Read Only) */}
      {!isEditing && readOnlyMode && (
        <div className="flex-shrink-0">
          <ChevronRight className="w-[18px] h-[18px] text-gray-400 transition-colors group-hover:text-gray-600" />
        </div>
      )}
    </div>
  );
};

export const LocationRow = memo(LocationRowComponent);
